import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PUBLIC_GEAR_URL } from '../../constants.js';
import { applyAffiliateTag } from '../../utils/amazon.js';

function GearCard({ product, affiliateTag }) {
  const [imgError, setImgError] = useState(false);
  const href = applyAffiliateTag(product.url, affiliateTag);
  const showImage = product.image && !imgError;

  return (
    <a
      className="gear-card"
      href={href}
      target="_blank"
      rel="noopener noreferrer sponsored"
    >
      {showImage
        ? (
          <img
            className="gear-card__img"
            src={product.image}
            alt={product.title}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        )
        : <div className="gear-card__img gear-card__img--placeholder" aria-hidden="true">🎧</div>}

      <div className="gear-card__body">
        <div className="gear-card__title">{product.title}</div>
        {product.price && <div className="gear-card__price">{product.price}</div>}
        {product.description && <p className="gear-card__desc">{product.description}</p>}
        <span className="gear-card__cta">View on Amazon →</span>
      </div>
    </a>
  );
}

GearCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
    image: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.string,
  }).isRequired,
  affiliateTag: PropTypes.string,
};

const Gear = () => {
  const [products, setProducts] = useState([]);
  const [affiliateTag, setAffiliateTag] = useState('');
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  useEffect(() => {
    let cancelled = false;
    fetch(PUBLIC_GEAR_URL)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        if (cancelled) return;
        setProducts(Array.isArray(data.products) ? data.products : []);
        setAffiliateTag(typeof data.affiliate_tag === 'string' ? data.affiliate_tag : '');
        setStatus('ready');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, []);

  const hasProducts = products.length > 0;

  return (
    <div className="gear-page">
      <h1>Our Gear</h1>
      <p className="gear-intro">
        The microphones and equipment we recommend for recording and enjoying ASMR.
      </p>

      {status === 'loading' && <p className="gear-note">Loading gear…</p>}

      {status === 'error' && (
        <p className="gear-note">
          Our gear list is taking a break. In the meantime, check out our{' '}
          <a href="https://ko-fi.com/asmrdotguru" target="_blank" rel="noopener noreferrer">
            Ko-fi shop
          </a>{' '}
          for high-end microphones.
        </p>
      )}

      {status === 'ready' && !hasProducts && (
        <p className="gear-note">
          We&apos;re curating our gear list right now — check back soon. In the meantime, our{' '}
          <a href="https://ko-fi.com/asmrdotguru" target="_blank" rel="noopener noreferrer">
            Ko-fi shop
          </a>{' '}
          has high-end microphones.
        </p>
      )}

      {status === 'ready' && hasProducts && (
        <>
          <div className="gear-grid">
            {products.map(p => (
              <GearCard key={p.id} product={p} affiliateTag={affiliateTag} />
            ))}
          </div>
          {affiliateTag !== '' && (
            <p className="gear-disclosure">
              As an Amazon Associate, ASMR.guru earns from qualifying purchases.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default Gear;
